"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.serviceController = void 0;
const ft_types_1 = require("@rivison-inc/ft-types");
const DateController_1 = require("../DateController");
const moment_1 = __importDefault(require("moment"));
const array_1 = require("../../utils/array");
/**
 * Controller for services
 */
class ServiceController {
    /**
     * Remove service defs that are disabled for the location
     * Replace frequency with override frequency
     */
    applyServiceOverrides(serviceTypes, overrideFrequencies) {
        const overrideFrequenciesByServiceType = array_1.createHashMap(overrideFrequencies, 'serviceTypeId');
        let updatedServiceTypes = [];
        serviceTypes.forEach(serviceType => {
            const overrideFrequency = overrideFrequenciesByServiceType[serviceType.id];
            if (overrideFrequency === null || overrideFrequency === void 0 ? void 0 : overrideFrequency.disabledForLocation) {
                return;
            }
            if ((overrideFrequency === null || overrideFrequency === void 0 ? void 0 : overrideFrequency.overrideFrequency) && serviceType.frequency !== (overrideFrequency === null || overrideFrequency === void 0 ? void 0 : overrideFrequency.overrideFrequency)) {
                serviceType.frequency = overrideFrequency.overrideFrequency;
                serviceType.hasFrequencyOverride = true;
                updatedServiceTypes.push(Object.assign(Object.assign({}, serviceType), { frequency: overrideFrequency.overrideFrequency, hasFrequencyOverride: true }));
            }
            updatedServiceTypes.push(serviceType);
        });
        return updatedServiceTypes;
    }
    /**
     * Remove service defs that are disabled for the location
     * Replace frequency with override frequency
     */
    removeDisabledServiceTypes(serviceTypes, siteTypeId) {
        const typeId = siteTypeId || 'noType';
        const enabledTypes = serviceTypes.filter((serviceType) => {
            const disabledTypes = serviceType.disabledSiteTypes || {};
            return !Boolean(disabledTypes[typeId]);
        });
        return enabledTypes;
    }
    /**
     * Get next dates for all services on a single piece of equipment
     * These services rely on each other so they must all be passed in
     */
    getServiceNextDatesForEquipment(serviceDefs, performedServices, equipment, timeZone, serviceMonth) {
        const manufactureDateAsDate = equipment.manufactureDate ? moment_1.default(equipment.manufactureDate).toDate() : moment_1.default().toDate();
        serviceDefs = serviceDefs.filter((serviceDef) => !serviceDef.unscheduled);
        const performedServicesPerServiceDef = this.addMissingPerformedServices(serviceDefs, performedServices, equipment);
        const performedServicesWithNextDates = this.getNextDates(serviceDefs, performedServicesPerServiceDef, manufactureDateAsDate, timeZone, serviceMonth);
        const performedServicesAfterAdvancedLogic = this.getAdvancedNextDates(serviceDefs, performedServicesWithNextDates, timeZone);
        const serviceDefsById = array_1.createHashMap(serviceDefs, 'id');
        const performedServicesAfterServiceMonths = performedServicesAfterAdvancedLogic.map((performedService) => {
            const serviceDef = serviceDefsById[performedService.serviceTypeId];
            const nextDateFromNow = this.applyClosestServiceMonth({
                nextDate: performedService.nextDateFromNow,
                serviceMonth,
                frequency: serviceDef === null || serviceDef === void 0 ? void 0 : serviceDef.frequency,
                frequencyType: serviceDef === null || serviceDef === void 0 ? void 0 : serviceDef.frequencyType
            });
            const nextDateFromScheduled = this.applyClosestServiceMonth({
                nextDate: performedService.nextDateFromScheduled,
                serviceMonth,
                frequency: serviceDef === null || serviceDef === void 0 ? void 0 : serviceDef.frequency,
                frequencyType: serviceDef === null || serviceDef === void 0 ? void 0 : serviceDef.frequencyType
            });
            if (performedService.nextDateFromScheduled !== nextDateFromScheduled) {
                performedService.affectedBy = performedService.affectedBy || [];
                if (!performedService.affectedBy.includes(ft_types_1.AffectedBy.SERVICE_MONTHS)) {
                    performedService.affectedBy.push(ft_types_1.AffectedBy.SERVICE_MONTHS);
                }
            }
            return Object.assign(Object.assign({}, performedService), { nextDateFromNow,
                nextDateFromScheduled });
        });
        return performedServicesAfterServiceMonths;
    }
    /**
     * Check if a service is due
     * @currentDate can be passed in to simulate a report from the past
     */
    isDue(serviceDef, dueDate, equipmentFieldData, performedServiceEquipmentFieldData, timeZone, currentDate) {
        var _a;
        const dueWhen = serviceDef.dueWhen;
        let dueServices = [];
        (_a = serviceDef.conditions) === null || _a === void 0 ? void 0 : _a.forEach((condition) => {
            if (condition.type === 'time') {
                const frequencyType = serviceDef.frequencyType;
                const now = currentDate || moment_1.default().toDate();
                const isSame = DateController_1.dateController.isSame(now, dueDate, frequencyType, timeZone);
                const isAfter = DateController_1.dateController.isAfter(now, dueDate, frequencyType, timeZone);
                const due = isSame || isAfter;
                dueServices.push(due);
            }
            else if (condition.type === 'metered') {
                const fieldData = equipmentFieldData;
                let data = parseFloat(fieldData[condition.meteredFieldId] || '0');
                const due = this.meteredServiceIsDue(condition, data, performedServiceEquipmentFieldData);
                dueServices.push(due);
            }
        });
        let isDue = false;
        if (dueWhen === 'all') {
            isDue = dueServices.every((isDue) => isDue);
        }
        else if (dueWhen === 'any') {
            isDue = Boolean(dueServices.filter((isDue) => isDue).length);
        }
        return isDue;
    }
    meteredServiceIsDue(condition, fieldData, performedServiceEquipmentFieldData) {
        const meteredAmount = parseFloat(condition.meteredAmount || '0');
        if (condition.meteredMethod === 'equal to') {
            return fieldData == meteredAmount;
        }
        else if (condition.meteredMethod === 'less than') {
            return fieldData < meteredAmount;
        }
        else if (condition.meteredMethod === 'greater than') {
            return fieldData > meteredAmount;
        }
        else if (condition.meteredMethod === 'not equal to') {
            return fieldData != meteredAmount;
        }
        else if (condition.meteredMethod === 'every') {
            const lastMeteredAmount = meteredAmount + parseFloat(performedServiceEquipmentFieldData[condition.meteredFieldId] || '0');
            return fieldData >= lastMeteredAmount;
        }
        return false;
    }
    /**
     * Check if a service is overdue
     * @currentDate can be passed in to simulate a report from the past
     */
    isOverdue(serviceDef, dueDate, timeZone, currentDate) {
        const frequencyType = serviceDef.frequencyType;
        const now = currentDate || moment_1.default().toDate();
        return DateController_1.dateController.isAfter(now, dueDate, frequencyType, timeZone);
    }
    /**
     * Replace month with closest service month
     * Within current year
     */
    applyClosestServiceMonth({ nextDate, serviceMonth, frequencyType, frequency }) {
        const ineligibleForServiceMonths = this.isIneligibleForServiceMonths({
            frequency,
            frequencyType
        });
        if (!serviceMonth || ineligibleForServiceMonths) {
            return moment_1.default(nextDate).toISOString();
        }
        const nextDateMonth = moment_1.default(nextDate).month();
        const months = [
            { num: 0, enabled: serviceMonth.january },
            { num: 1, enabled: serviceMonth.february },
            { num: 2, enabled: serviceMonth.march },
            { num: 3, enabled: serviceMonth.april },
            { num: 4, enabled: serviceMonth.may },
            { num: 5, enabled: serviceMonth.june },
            { num: 6, enabled: serviceMonth.july },
            { num: 7, enabled: serviceMonth.august },
            { num: 8, enabled: serviceMonth.september },
            { num: 9, enabled: serviceMonth.october },
            { num: 10, enabled: serviceMonth.november },
            { num: 11, enabled: serviceMonth.december },
        ];
        const enabledMonths = months.filter((month) => month.enabled);
        const enabledMonthNumbers = enabledMonths.map((month) => month.num);
        if (!enabledMonthNumbers.length) {
            return moment_1.default(nextDate).toISOString();
        }
        const closestMonth = enabledMonthNumbers.reduce((a, b) => {
            return Math.abs(b - nextDateMonth) <= Math.abs(a - nextDateMonth) ? b : a;
        });
        const newNextDate = moment_1.default(nextDate).set("month", closestMonth);
        return newNextDate.toISOString();
    }
    formatDateByFrequencyType(date, frequencyType, timeZone, options) {
        const longFormats = {
            [ft_types_1.FrequencyType.MINUTE]: 'MMMM D, YYYY h:mma',
            [ft_types_1.FrequencyType.HOUR]: 'MMMM D, YYYY ha',
            [ft_types_1.FrequencyType.DAY]: 'MMMM D, YYYY',
            [ft_types_1.FrequencyType.WEEK]: 'MMMM D, YYYY',
            [ft_types_1.FrequencyType.MONTH]: 'MMMM YYYY',
            [ft_types_1.FrequencyType.YEAR]: 'YYYY',
        };
        const shortFormats = {
            [ft_types_1.FrequencyType.MINUTE]: 'YYYY-MM-DD h:mma',
            [ft_types_1.FrequencyType.HOUR]: 'YYYY-MM-DD ha',
            [ft_types_1.FrequencyType.DAY]: 'YYYY-MM-DD',
            [ft_types_1.FrequencyType.WEEK]: 'YYYY-MM-DD',
            [ft_types_1.FrequencyType.MONTH]: 'YYYY-MM',
            [ft_types_1.FrequencyType.YEAR]: 'YYYY',
        };
        const formats = (options === null || options === void 0 ? void 0 : options.short) ? shortFormats : longFormats;
        const format = formats[frequencyType] || 'MMMM D YYYY h:mmA';
        return moment_1.default(date).tz(timeZone).format(format);
    }
    // =========================== Private ===========================
    /**
     * Service months does not really make sense for less than monthly inspections
     *
     * @note We may want to add more advanced checking for minute, hour, day later
     */
    isIneligibleForServiceMonths(params) {
        // frequency and frequencyType are passed as a string sometimes, which seems like a failure of our type-checking somewhere
        const frequencyType = Number(params.frequencyType);
        const frequency = Number(params.frequency);
        if (frequencyType === ft_types_1.FrequencyType.MINUTE || frequencyType === ft_types_1.FrequencyType.HOUR || frequencyType === ft_types_1.FrequencyType.DAY) {
            return true;
        }
        if (frequencyType === ft_types_1.FrequencyType.MONTH && frequency === 1) {
            return true;
        }
        return false;
    }
    /**
     * Add a default performed service record for
     * those that have never been performed before
     */
    addMissingPerformedServices(serviceDefs, performedServices, equipment) {
        const performedServicesByType = array_1.createHashMap(performedServices, 'serviceTypeId');
        return serviceDefs.map((serviceDef) => {
            const defaultPerformedService = { serviceTypeId: serviceDef.id, equipmentId: equipment.id };
            const previousPerformedService = performedServicesByType[serviceDef.id];
            return previousPerformedService || defaultPerformedService;
        });
    }
    /**
     * generate next dates for each
     * service def before advanced logic
     */
    //todo: why is serviceMonth not used
    getNextDates(serviceDefs, performedServices, manufactureDate, timeZone, serviceMonth) {
        const performedServicesByType = array_1.createHashMap(performedServices, 'serviceTypeId');
        return serviceDefs.map((serviceDef) => {
            const performedService = performedServicesByType[serviceDef.id];
            const performedServiceFromDate = performedService.lastDate || manufactureDate;
            const nextDateFromNow = serviceDef.frequencyType ? DateController_1.dateController.increaseDateByFrequency(serviceDef.frequencyType, serviceDef.frequency, moment_1.default().toDate(), timeZone) : undefined;
            const nextDateFromScheduled = serviceDef.frequencyType ? DateController_1.dateController.increaseDateByFrequency(serviceDef.frequencyType, serviceDef.frequency, performedServiceFromDate, timeZone) : undefined;
            const affectedBy = [...performedService.affectedBy || []];
            if (serviceDef.hasFrequencyOverride) {
                if (!affectedBy.includes(ft_types_1.AffectedBy.LOCATION_OVERRIDE)) {
                    affectedBy.push(ft_types_1.AffectedBy.LOCATION_OVERRIDE);
                }
            }
            if (performedService.overidedNextDate) {
                if (!affectedBy.includes(ft_types_1.AffectedBy.SERVICE_OVERRIDE)) {
                    affectedBy.push(ft_types_1.AffectedBy.SERVICE_OVERRIDE);
                }
            }
            return Object.assign(Object.assign({}, performedService), { affectedBy,
                nextDateFromNow, nextDateFromScheduled: performedService.overidedNextDate || nextDateFromScheduled });
        });
    }
    /**
     * Overide generated next dates for service
     * defs that are changed by advanced logic
     */
    getAdvancedNextDates(serviceDefs, performedServices, timeZone) {
        const serviceDefsById = array_1.createHashMap(serviceDefs, 'id');
        const serviceDefsByAdvancedChangeTypeId = array_1.createHashMap(serviceDefs, 'advancedServiceToChangeTypeId');
        const performedServicesByServiceTypeId = array_1.createHashMap(performedServices, 'serviceTypeId');
        return performedServices.map((performedService) => {
            const serviceDef = serviceDefsById[performedService.serviceTypeId];
            const advancedLogicServiceDef = serviceDefsByAdvancedChangeTypeId[performedService.serviceTypeId];
            if (!advancedLogicServiceDef) {
                return performedService;
            }
            const performedServiceFromAdvanced = performedServicesByServiceTypeId[advancedLogicServiceDef.id];
            const types = ['nextDateFromNow', 'nextDateFromScheduled'];
            for (const dateKeyIdx in types) {
                const dateKey = types[dateKeyIdx];
                if (advancedLogicServiceDef.advancedAction === ft_types_1.ADVANCED_ACTION.AVOID_SAME_DATE) {
                    // We need to check both next and last in case a yearly is done early
                    // or so the monthly doesnt go back a month when a yearly is performed 
                    const sameAsNextDate = DateController_1.dateController.isSame(performedService[dateKey], performedServiceFromAdvanced[dateKey], serviceDef.frequencyType, timeZone);
                    const sameAsLastDate = performedServiceFromAdvanced.lastDate && DateController_1.dateController.isSame(performedService[dateKey], performedServiceFromAdvanced.lastDate, serviceDef.frequencyType, timeZone);
                    if (sameAsNextDate || sameAsLastDate) {
                        performedService[dateKey] = DateController_1.dateController.increaseDateByFrequency(serviceDef.frequencyType, serviceDef.frequency, performedService[dateKey], timeZone);
                        performedService.affectedBy = performedService.affectedBy || [];
                        if (!performedService.affectedBy.includes(ft_types_1.AffectedBy.ANOTHER_SERVICE)) {
                            performedService.affectedBy.push(ft_types_1.AffectedBy.ANOTHER_SERVICE);
                        }
                    }
                }
                if (advancedLogicServiceDef.advancedAction === ft_types_1.ADVANCED_ACTION.KEEP_X_TIME_AWAY) {
                    if (DateController_1.dateController.isAfter(performedService[dateKey], performedServiceFromAdvanced[dateKey], serviceDef.frequencyType, timeZone)) {
                        performedService[dateKey] = DateController_1.dateController.increaseDateByFrequency(serviceDef.frequencyType, advancedLogicServiceDef.advancedAmount || 0, performedServiceFromAdvanced[dateKey], timeZone);
                    }
                    else {
                        performedService[dateKey] = DateController_1.dateController.subtractDateByFrequency(serviceDef.frequencyType, advancedLogicServiceDef.advancedAmount || 0, performedServiceFromAdvanced[dateKey], timeZone);
                    }
                    performedService.affectedBy = performedService.affectedBy || [];
                    if (!performedService.affectedBy.includes(ft_types_1.AffectedBy.ANOTHER_SERVICE)) {
                        performedService.affectedBy.push(ft_types_1.AffectedBy.ANOTHER_SERVICE);
                    }
                }
            }
            return performedService;
        });
    }
}
exports.serviceController = new ServiceController();
